<template>
    <menu-button
        :color="selection.selectedItemsCount.value === 0 ? 'secondary' : 'primary'"
        :disabled="selection.selectedItemsCount.value === 0"
        :toggle-classes="{ 'btn-icon': isMobile }"
        :tooltip="noItemSelectedTooltip"
    >
        <template #title>
            <div v-if="isDesktop" class="flex items-center justify-between gap-2">{{ trans('general.action.export') }} <chevron-down /></div>
            <icon v-else name="cil-data-transfer-down" />
        </template>
        <template #menu="{ close }">
            <div class="p-3 pb-2 text-nowrap">
                <div class="pb-1">
                    {{ trans('general.export.description') }}
                </div>
                <div v-if="exportError" class="flex items-start gap-1 text-danger">
                    <div style="line-height: 16px">
                        <exclamation-mark />
                    </div>
                    <div>
                        <h3 class="mb-0" style="font-size: 15px; font-weight: normal">
                            {{ exportError.error }}
                        </h3>
                        <p class="text-small mb-0">
                            {{ exportError.help }}
                        </p>
                    </div>
                </div>
            </div>

            <div v-if="currentExports.length > 0" class="border-top relative max-h-45 overflow-y-auto">
                <div class="p-3 pb-2">
                    <div class="sticky top-0 z-1 mb-2 flex items-center justify-between bg-white">
                        <div class="text-small text-muted">
                            {{ trans('general.export.current_exports') }}&nbsp;<span v-if="currentExports.length">({{ currentExports.length }})</span>
                        </div>
                        <button class="btn btn-link btn-sm text-decoration-none p-0" type="button" @click="clearCompleted">
                            {{ trans('general.action.clear_completed') }}
                        </button>
                    </div>
                    <div class="flex flex-col gap-4">
                        <export-item
                            v-for="exportItem in currentExports"
                            :key="exportItem.id"
                            :item="exportItem"
                            :progress="getProgress(exportItem)"
                            @download="downloadExport"
                            @retry="retryExport"
                        />
                    </div>
                </div>
            </div>

            <export-button
                v-if="props.allowedExports.includes('pdf')"
                :disabled="pdfExportDisabled"
                :disabled-message="pdfExportDisabledMessage"
                :filters="activeQueryParams.urlParams()"
                :group-key="exportGroupKey"
                :non-query-filters="nonQueryFilters"
                :route="routeName"
                :selection="selection.getSelectionData()"
                type="pdf"
                @done="close"
                @error="onError"
            >
                <span class="flex items-center gap-1">
                    <icon name="cil-notes" />
                    {{ trans('general.file.pdf') }}
                </span>
            </export-button>
            <export-button
                v-if="props.allowedExports.includes('csv')"
                :filters="activeQueryParams.urlParams()"
                :group-key="exportGroupKey"
                :non-query-filters="nonQueryFilters"
                :route="routeName"
                :selection="selection.getSelectionData()"
                type="csv"
                @done="close"
                @error="onError"
            >
                <span class="flex items-center gap-1">
                    <grid />
                    {{ trans('general.file.csv') }}
                </span>
            </export-button>
            <export-button
                v-if="props.allowedExports.includes('xlsx')"
                :filters="activeQueryParams.urlParams()"
                :group-key="exportGroupKey"
                :non-query-filters="nonQueryFilters"
                :route="routeName"
                :selection="selection.getSelectionData()"
                type="xlsx"
                @done="close"
                @error="onError"
            >
                <span class="flex items-center gap-1">
                    <grid />
                    {{ trans('general.file.xlsx') }}
                </span>
            </export-button>
        </template>
    </menu-button>
</template>
<script setup>
import { isDesktop, isMobile } from '@/Utils/UseMedia';
import ExportButton from '@/Pages/TestRecords/ExportButton.vue';
import ExclamationMark from '../../icons/ExclamationMark.vue';
import Grid from '../../icons/Grid.vue';
import MenuButton from '@/Components/MenuButton.vue';
import { computed, ref } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { trans } from '@/mixins/i18n.js';
import { useExportsStore } from '@/stores/exports.js';
import ExportItem from '@/Components/ExportItem.vue';
import ChevronDown from '../../icons/ChevronDown.vue';

const props = defineProps({
    selection: Object,
    activeQueryParams: Object,
    nonQueryFilters: Object,
    routeName: String,
    exportGroupKey: {
        type: String,
        required: true,
    },
    noItemSelectedTooltip: String,
    allowedExports: {
        type: Array,
        default: () => ['pdf', 'csv', 'xlsx'],
    },
});

const exportError = ref(null);
const exportsStore = useExportsStore();
const page = usePage();

const currentExports = exportsStore.getExportsByGroup(props.exportGroupKey);

// PDF export renders one document per reference, so it's capped (see config/exports.php). When the
// selection exceeds the cap the PDF option is greyed out with an explanatory message.
const maxPdfReferences = computed(() => page.props.settings?.maxPdfReferences ?? null);
const pdfExportDisabled = computed(() => maxPdfReferences.value != null && props.selection.selectedItemsCount.value > maxPdfReferences.value);
const pdfExportDisabledMessage = computed(() =>
    pdfExportDisabled.value ? trans('general.export.pdf_limit', { max: maxPdfReferences.value }) : null,
);

const getProgress = (exportItem) => {
    return exportsStore.getProgress(exportItem);
};

const downloadExport = (exportItem) => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = exportItem.url;
    document.body.appendChild(iframe);
    setTimeout(() => iframe.remove(), 300_000);
};

const retryExport = async (exportId) => {
    try {
        await exportsStore.retryExport(exportId, props.exportGroupKey);
    } catch (error) {
        console.error('Failed to retry export:', error);
    }
};

const clearCompleted = () => {
    exportsStore.clearCompletedExports(props.exportGroupKey);
};

const onError = ({ id, error }) => {
    exportError.value = error;
};
</script>
